import React, { useEffect, useRef, useState } from "react";
import { MailCheck, RefreshCw, Loader2, ArrowRight, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Btn, Input } from "../UI";

const errMsg = (e, fb) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : d?.message || fb; };

/* Step 4 of entity onboarding: auto-sends the OTP, visible Resend with server-driven cooldown,
   remaining-sends counter (5 / 30 min), inline "change email", spam hint. */
export const VerifyEmailStep = ({ entity, email, onEmailChanged, onVerified, onFinish }) => {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [otp, setOtp] = useState("");
  const [verified, setVerified] = useState(false);
  const [info, setInfo] = useState(null);
  const [cooldown, setCooldown] = useState(0);
  const [editing, setEditing] = useState(false);
  const [newEmail, setNewEmail] = useState(email || "");
  const autoRef = useRef(false);

  const applyQuota = (data, fallback) => {
    setInfo(data);
    setCooldown(Math.max(0, parseInt(data?.resend_available_in ?? fallback ?? 0) || 0));
  };

  const sendOtp = async ({ force = false } = {}) => {
    if (sending || (cooldown > 0 && !force)) return;
    setSending(true);
    try {
      const { data } = await api.post(`/political-os/entities/${entity.id}/verify-email/send`);
      setSent(true); setOtp(""); applyQuota(data, 45);
      toast.success(`Code sent to ${data.sent_to}`);
    } catch (e) {
      if (e?.response?.status === 429) {
        setSent(true);
        setCooldown(parseInt(e.response.headers?.["retry-after"]) || 60);
      }
      toast.error(errMsg(e, "Could not send code"));
    } finally { setSending(false); }
  };

  useEffect(() => {
    if (!entity?.id || verified || autoRef.current) return;
    autoRef.current = true;
    (async () => {
      try {
        const { data } = await api.get(`/political-os/entities/${entity.id}/verify-email/status`);
        if (data.pending) { setSent(true); applyQuota(data); return; }
      } catch { /* fall through to a fresh send */ }
      sendOtp();
    })();
  }, [entity?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const confirmOtp = async () => {
    try {
      await api.post(`/political-os/entities/${entity.id}/verify-email/confirm`, { code: otp });
      setVerified(true); toast.success("Official email verified"); onVerified?.();
    } catch (e) {
      if (e?.response?.status === 429) { setOtp(""); setInfo((i) => ({ ...i, attempts_left: 0 })); }
      toast.error(errMsg(e, "Verification failed"));
    }
  };

  const saveEmail = async () => {
    try {
      const { data } = await api.put(`/political-os/entities/${entity.id}/official-email`, { email: newEmail.trim() });
      onEmailChanged?.(data.official_email);
      setEditing(false); setSent(false); setCooldown(0);
      toast.success("Email updated — sending a new code");
      setTimeout(() => sendOtp({ force: true }), 0);
    } catch (e) { toast.error(errMsg(e, "Could not update email")); }
  };

  const fromAddr = info?.from_address || "verify@totalreach.us";
  const cooldownLabel = cooldown >= 90 ? `${Math.ceil(cooldown / 60)} min` : `${cooldown}s`;

  return (
    <div className="space-y-5 text-center py-4" data-testid="verify-email-step">
      <MailCheck className={`w-12 h-12 mx-auto ${verified ? "text-emerald-300" : "text-cyan-300"}`} />
      {verified ? (
        <>
          <div className="font-display text-xl font-bold text-emerald-300">Official email verified</div>
          <p className="text-sm text-slate-400">Your entity's verification score just went up. Website verification runs automatically in the background.</p>
        </>
      ) : (
        <>
          <div className="font-display text-xl font-bold">Verify your official campaign email</div>
          {!editing ? (
            <p className="text-sm text-slate-400" data-testid="otp-sent-to">
              {sent ? "We sent a 6-digit code to" : sending ? "Sending a 6-digit code to" : "We'll send a 6-digit code to"}{" "}
              <b className="text-slate-200">{email}</b>{" "}
              <button onClick={() => { setNewEmail(email || ""); setEditing(true); }} className="inline-flex items-center gap-1 text-xs text-cyan-300 hover:text-cyan-200 align-middle" data-testid="change-email-btn">
                <Pencil className="w-3 h-3" /> Wrong address?
              </button>
            </p>
          ) : (
            <div className="flex items-end justify-center gap-2 flex-wrap" data-testid="change-email-form">
              <Input label="Official campaign email" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className="w-72 text-left" data-testid="change-email-input" />
              <Btn onClick={saveEmail} disabled={!newEmail.includes("@")} data-testid="change-email-save">Save & send code</Btn>
              <button onClick={() => setEditing(false)} className="text-xs text-slate-500 hover:text-slate-300 pb-3">Cancel</button>
            </div>
          )}

          {sent && (
            <div className="flex items-center justify-center gap-2">
              <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" data-testid="otp-input"
                inputMode="numeric" autoComplete="one-time-code"
                className="w-40 text-center text-2xl tracking-[8px] bg-slate-900/80 border border-white/15 rounded-xl px-3 py-3 focus:border-cyan-400/60 focus:outline-none font-mono" />
              <Btn onClick={confirmOtp} disabled={otp.length !== 6} data-testid="confirm-otp-btn">Verify</Btn>
            </div>
          )}

          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Btn variant="secondary" onClick={() => sendOtp()} disabled={sending || cooldown > 0} data-testid="resend-otp-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {sending ? "Sending…" : cooldown > 0 ? `Resend in ${cooldownLabel}` : sent ? "Resend code" : "Send verification code"}
            </Btn>
            {info && (
              <span className="text-[11px] text-slate-500" data-testid="otp-quota">
                {info.remaining_sends} of {info.limit ?? 5} sends left in this {info.window_minutes ?? 30}-minute window
                {typeof info.attempts_left === "number" && sent ? ` · ${info.attempts_left} attempts left on this code` : ""}
              </span>
            )}
          </div>
          <p className="text-[11px] text-slate-500 max-w-md mx-auto" data-testid="otp-spam-hint">
            Not in your inbox within a minute? Check <b>Spam / Junk</b> — the message comes from <span className="text-slate-300">{fromAddr}</span> with subject "TotalReach verification code". Codes expire in {info?.expires_in_minutes ?? 15} minutes.
          </p>
        </>
      )}
      <div className="pt-4 flex items-center justify-center gap-3">
        {!verified && <button onClick={onFinish} className="text-xs text-slate-500 hover:text-slate-300" data-testid="skip-verify-btn">Skip — verification pending</button>}
        {verified && <Btn onClick={onFinish} data-testid="finish-onboarding-btn">Continue <ArrowRight className="w-4 h-4" /></Btn>}
      </div>
    </div>
  );
};
